// ===========================================
// Security Audit Log Page
// ===========================================
// Displays security audit logs for admin review

import { useState } from 'react';
import {
  Box,
  Alert,
  Button,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  TablePagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { format } from 'date-fns';
import { useSecurityAuditLogs, useAuditActions } from '../../hooks/useSecurityAudit';
import { useTeachers, useParents } from '../../hooks/useUsers';
import { SecurityAuditFilters, SecurityAuditLog } from '../../api/securityAudit.api';
import { Teacher, Parent } from '../../api/users.api';
import { actionLabel, auditDetailsSummary, formatAuditTime } from './securityAuditSummary';

// ===========================================
// HELPER COMPONENTS
// ===========================================

/** The raw action stays in the tooltip for support; the chip shows the label (#616). */
function ActionChip({ action, labels }: { action: string; labels: ReadonlyMap<string, string> }) {
  const color = action.includes('GRANTED') ? 'success' : 'warning';
  return (
    <Tooltip title={action}>
      <Chip label={actionLabel(action, labels)} size="small" color={color} />
    </Tooltip>
  );
}

/** The details column: a one-line summary and the toggle. The JSON opens in a row of its own. */
function DetailsCell({
  details,
  expanded,
  onToggle,
}: {
  details: Record<string, unknown>;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (!hasDetails(details)) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
        {auditDetailsSummary(details) || 'Details available'}
      </Typography>
      <IconButton
        size="small"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide details' : 'Show details'}
      >
        {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
      </IconButton>
    </Stack>
  );
}

const hasDetails = (details: Record<string, unknown>) => Boolean(details) && Object.keys(details).length > 0;

const COLUMN_COUNT = 6;

function AuditLogRow({ log, labels }: { log: SecurityAuditLog; labels: ReadonlyMap<string, string> }) {
  const [expanded, setExpanded] = useState(false);
  const open = expanded && hasDetails(log.details);

  return (
    <>
      {/* No bottom border while the details row below belongs to it. */}
      <TableRow hover sx={open ? { '& > td': { borderBottom: 'none' } } : undefined}>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2">{formatAuditTime(log.createdAt)}</Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <ActionChip action={log.action} labels={labels} />
        </TableCell>
        <TableCell>
          <Tooltip title={log.performedByUser.email}>
            <Typography variant="body2">
              {log.performedByUser.firstName} {log.performedByUser.lastName}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={log.targetUser.email}>
            <Typography variant="body2">
              {log.targetUser.firstName} {log.targetUser.lastName}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <DetailsCell details={log.details} expanded={open} onToggle={() => setExpanded((e) => !e)} />
        </TableCell>
        <TableCell>
          <Tooltip title={log.userAgent || 'Unknown'}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
              {log.ipAddress || 'Unknown'}
            </Typography>
          </Tooltip>
        </TableCell>
      </TableRow>
      {/* The full details open in a row of their own across the table, so the row above keeps its
          layout (staging walk, 26/10/2026). Mounted only while open: a hidden <pre> inside a cell
          still takes its full width in the table's layout. */}
      {open && (
        <TableRow>
          <TableCell colSpan={COLUMN_COUNT} sx={{ pt: 0 }}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
              <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(log.details, null, 2)}
              </pre>
            </Paper>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function SecurityAuditPage() {
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filter state
  const [filters, setFilters] = useState<SecurityAuditFilters>({});

  // Fetch data
  const { data, isLoading, error } = useSecurityAuditLogs(
    filters,
    rowsPerPage,
    page * rowsPerPage
  );
  const { data: actions } = useAuditActions();
  const labels = new Map((actions ?? []).map((a) => [a.value, a.label] as const));
  const { data: teachers } = useTeachers();
  const { data: parents } = useParents();

  // Combine teachers and parents for target user filter
  const allUsers = [
    ...(teachers?.map((t: Teacher) => ({
      id: t.userId,
      name: `${t.User.firstName} ${t.User.lastName}`,
      role: 'Teacher' as const,
    })) || []),
    ...(parents?.map((p: Parent) => ({
      id: p.userId,
      name: `${p.contact1FirstName} ${p.contact1LastName}`,
      role: 'Parent' as const,
    })) || []),
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Handlers
  const handleActionChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFilters({ ...filters, action: value || undefined });
    setPage(0);
  };

  const handleTargetUserChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFilters({ ...filters, targetUserId: value || undefined });
    setPage(0);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters({
      ...filters,
      startDate: value ? new Date(value).toISOString() : undefined,
    });
    setPage(0);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters({
      ...filters,
      endDate: value ? new Date(value).toISOString() : undefined,
    });
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    if (!data?.data.length) return;

    // Build CSV content
    const headers = ['Date & Time', 'Action', 'Performed By', 'Performed By Email', 'Target User', 'Target Email', 'Details', 'IP Address', 'User Agent'];
    const rows = data.data.map((log) => [
      format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm:ss'),
      actionLabel(log.action, labels),
      `${log.performedByUser.firstName} ${log.performedByUser.lastName}`,
      log.performedByUser.email,
      `${log.targetUser.firstName} ${log.targetUser.lastName}`,
      log.targetUser.email,
      JSON.stringify(log.details).replace(/"/g, '""'),
      log.ipAddress || '',
      log.userAgent || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `security-audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <PageHeader
        title="Security Audit Log"
        subtitle="View role changes and security-related events"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Security Audit' },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load audit logs. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={filters.action || ''}
              onChange={handleActionChange}
              label="Action Type"
            >
              <MenuItem value="">All Actions</MenuItem>
              {actions?.map((action) => (
                <MenuItem key={action.value} value={action.value}>
                  {action.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Target User</InputLabel>
            <Select
              value={filters.targetUserId || ''}
              onChange={handleTargetUserChange}
              label="Target User"
            >
              <MenuItem value="">All Users</MenuItem>
              {allUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} ({user.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            type="date"
            label="Start Date"
            size="small"
            InputLabelProps={{ shrink: true }}
            onChange={handleStartDateChange}
          />

          <TextField
            type="date"
            label="End Date"
            size="small"
            InputLabelProps={{ shrink: true }}
            onChange={handleEndDateChange}
          />

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={!data?.data.length}
            sx={{ ml: 'auto' }}
          >
            Export CSV
          </Button>
        </Stack>
      </Paper>

      {/* Audit Log Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Date & Time</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Performed By</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Target User</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Details</TableCell>
              <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>IP Address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading audit logs...</Typography>
                </TableCell>
              </TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No audit logs found. Security-related events will appear here.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((log) => <AuditLogRow key={log.id} log={log} labels={labels} />)
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.pagination.total ?? 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
}
